// components/BatchProcessing/BatchProcessing.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  Alert,
  LinearProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { batchProcessingService } from '../../services/batchProcessing.service';

import BatchItemCard from './BatchItemCard';
import BatchProcessingStatus from './BatchProcessingStatus';
import BatchResults from './BatchResults';

const BatchProcessing = ({ onBack }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [batchItems, setBatchItems] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [fatalError, setFatalError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down(500));
  const steps = ['Configurar Lote', 'Processamento', 'Resultados'];
  const getStepLabel = (label, index) => {
    if (isVerySmallScreen) {
      const shortLabels = ['Dados', 'Processo', 'Resultado'];
      return shortLabels[index] || label;
    }
    if (isSmallScreen) {
      const mediumLabels = ['Config.', 'Process.', 'Result.'];
      return mediumLabels[index] || label;
    }
    return label;
  };
  const addBatchItem = () => {
    setBatchItems(prev => [...prev, {
      id: Date.now() + Math.random(),
      axisFile: null,
      stakeFile: null,
      imgFile: null,
      processingMethod: 'METODO_3_COMPLETO',
      taskName: '',
      startPointLabel: '0+000',
      startNameColumn: 'Name',
      crs: 'EPSG:31982',
      eixoDirection: 'AUTOMATICO',
      axisFileName: '',
      stakeFileName: '',
      imgFileName: ''
    }]);
  };

  const removeBatchItem = (id) => {
    setBatchItems(prev => prev.filter(item => item.id !== id));
  };

  // Manipular upload de arquivo
  const handleFileUpload = (event, fieldName, itemId) => {
    const file = event.target.files[0];
    if (!file) return;

    setBatchItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          [fieldName]: file,
          [`${fieldName}Name`]: file.name
        };
      }
      return item;
    }));
  };

  // Atualizar parâmetros do item
  const updateItemParam = (itemId, field, value) => {
    setBatchItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // Remover arquivo específico
  const handleRemoveFile = (itemId, fieldName) => {
    setBatchItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          [fieldName]: null,
          [`${fieldName}Name`]: ''
        };
      }
      return item;
    }));
  };

  // Validar se o lote está pronto para processamento
  const isBatchValid = () => {
    return batchItems.length > 0 && 
           batchItems.every(item => item.axisFile && item.imgFile);
  };

  // Validar item individual
  const isItemValid = (item) => {
    return item.axisFile && item.imgFile;
  };

  // Iniciar processamento em lote
  const handleProcessBatch = async () => {
    setProcessing(true);
    setError(null);
    setSuccess(null);
    setActiveStep(1);

    try {
      const batchData = {
        items: batchItems.map(item => ({
          axisFile: item.axisFile,
          stakeFile: item.stakeFile,
          imgFile: item.imgFile,
          processingMethod: item.processingMethod,
          taskName: item.taskName,
          startPointLabel: item.startPointLabel,
          startNameColumn: item.startNameColumn,
          crs: item.crs,
          eixoDirection: item.eixoDirection
        }))
      };

      const response = await batchProcessingService.processBatch(batchData);
      setBatchId(response.batch_id);
      
      // Iniciar polling do status
      startStatusPolling(response.batch_id);
      
      setSuccess(`Lote iniciado com ${response.total_tasks} tarefas`);
      
    } catch (err) {
      setError(err.message);
      setActiveStep(0);
      setProcessing(false);
    }
  };

  // Polling do status do lote
  const startStatusPolling = (batchId) => {
    const interval = setInterval(async () => {
      try {
        const status = await batchProcessingService.getBatchStatus(batchId);
        setBatchStatus(status);
        
        if (status.status === 'COMPLETED' || status.status === 'FAILED' || status.status === 'PARTIALLY_COMPLETED') {
          clearInterval(interval);
          setPollingInterval(null);
          setActiveStep(2);
          setProcessing(false);
          
          if (status.status === 'COMPLETED') {
            setSuccess('Todos os processamentos foram concluídos com sucesso!');
          } else if (status.status === 'PARTIALLY_COMPLETED') {
            setSuccess(`Processamento parcialmente concluído: ${status.completed_tasks}/${status.total_tasks} tarefas`);
          } else {
            setError('Alguns processamentos falharam');
          }
        }
      } catch (err) {
        console.error('Erro ao verificar status:', err);
        clearInterval(interval);
        setPollingInterval(null);
        setProcessing(false);

        setFatalError("Falha ao consultar o status do processamento. O servidor não respondeu.");

        setActiveStep(2);
      }
    }, 3000);
    
    setPollingInterval(interval);
  };

  // Download dos resultados
  const handleDownloadBatch = async () => {
    try {
      await batchProcessingService.downloadBatchResults(batchId);
      setSuccess('Download do lote iniciado!');
    } catch (err) {
      setError(err.message);
    }
  };

  // Cancelar processamento
  const handleCancelProcessing = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setActiveStep(0);
    setProcessing(false);
    setBatchStatus(null);
  };

  // Resetar formulário
  const resetBatch = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    setBatchItems([]);
    setActiveStep(0);
    setProcessing(false);
    setError(null);
    setSuccess(null);
    setBatchId(null);
    setBatchStatus(null);
    setFatalError(null);
  };

  return (
    <>
      {/* <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }} elevation={3}>         */}
       
        {/* <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }} align="center">
          Processe múltiplos eixos rodoviários simultaneamente
        </Typography> */}

        {/* Stepper */}
        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 4,
            '& .MuiStepLabel-root': {
              padding: { xs: '4px', sm: '8px' }
            }
          }}
          orientation={isVerySmallScreen ? "vertical" : "horizontal"}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: { 
                      xs: '0.7rem', 
                      sm: '0.8rem', 
                      md: '0.875rem' 
                    },
                    textAlign: 'center'
                  }
                }}
              >
                {getStepLabel(label, index)}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* PASSO 1: Configuração do Lote */}
        {activeStep === 0 && (
          <Box>
            {batchItems.length === 0 ? (
              <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
                <CardContent>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Nenhum conjunto adicionado
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Adicione conjuntos de arquivos para processamento em lote
                  </Typography>
                  <Button variant="contained" onClick={addBatchItem}>
                    Adicionar Primeiro Conjunto
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {batchItems.map((item, index) => (
                  <Grid item xs={12} key={item.id}>
                    <BatchItemCard
                      item={item}
                      index={index}
                      onRemove={removeBatchItem}
                      onFileUpload={handleFileUpload}
                      onParamUpdate={updateItemParam}
                      onRemoveFile={(fieldName) => handleRemoveFile(item.id, fieldName)}
                      isValid={isItemValid(item)}
                      disabled={processing}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 3 }}>
              <Typography variant="h6">
                Configuração do Lote ({batchItems.length} itens)
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addBatchItem}
                disabled={processing}
              >
                Adicionar Conjunto
              </Button>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={onBack}
                disabled={processing}
              >
                Voltar ao Processamento Único
              </Button>
              
              <Button
                variant="contained"
                onClick={handleProcessBatch}
                disabled={!isBatchValid() || processing}
                size="large"
              >
                Iniciar Processamento em Lote
              </Button>
            </Box>
          </Box>
        )}

        {/* PASSO 2: Processamento */}
        {activeStep === 1 && (
          <BatchProcessingStatus
            batchStatus={batchStatus}
            totalItems={batchItems.length}
            onCancel={handleCancelProcessing}
          />
        )}

        {/* PASSO 3: Resultados */}
        {activeStep === 2 && (
          <BatchResults
            batchStatus={batchStatus}
            fatalError={fatalError}
            onDownload={handleDownloadBatch}
            onReset={resetBatch}
            onBack={onBack}
          />
        )}
      {/* </Paper> */}
    </>
  );
};

export default BatchProcessing;